// Dexie database utility for offline data management
import Dexie, { Table } from 'dexie';
import { applyEncryptionMiddleware } from 'dexie-encrypted';
import { createLogger, LogLevel } from '../lib/logger';
import { Client } from './types/client';
import { Contact } from './types/contact';
import { DocumentTemplate } from './types/document_template';
import { Document } from './types/document';
import { ApplicationSession } from './types/application_session';
import { BiometricCredential } from './types/biometric_credential';
import { Signature, scopedLocalId } from './types/signature';
import { AnalyticsEvent } from './types/analytics_event';
import { HelpDocument } from './types/help_document';
import { Program, Site } from './types/user';
import { HTTP_CACHE_SCHEMA, HttpCacheEntry } from './types/http_cache';
import { BIOMETRICS_DISABLED } from '../config/env';

const logger = createLogger('DB', LogLevel.WARN);

/**
 * MonAmiDatabase - The main database class for offline data storage
 * 
 * Example usage:
 * import db from '@/db';
 * 
 * // Store clients
 * await db.saveClients(clientsArray);
 * 
 * // Get all clients
 * const allClients = await db.clients.toArray();
 * 
 * // Get a client by id
 * const client = await db.clients.get(123);
 */
class MonAmiDatabase extends Dexie {
  // Define tables
  clients!: Table<Client, number>;
  contacts!: Table<Contact, number | string>; // string UUID for drafts, number for synced
  documentTemplates!: Table<DocumentTemplate, number>;
  documents!: Table<Document, number | string>; // string UUID for drafts, number for synced
  applicationSession!: Table<ApplicationSession, string>;
  biometricCredentials!: Table<BiometricCredential, number>;
  signatures!: Table<Signature, string>;
  helpDocuments!: Table<HelpDocument, string>;
  analyticsEvents!: Table<AnalyticsEvent, string>;
  programs!: Table<Program & { organization_id: number }, number>;
  sites!: Table<Site & { organization_id: number }, number>;
  httpCache!: Table<HttpCacheEntry, string>;

  constructor() {
    super('monami_offline_db');

    // Version 21 - Reset schema to clean state (consolidating all previous migrations)
    // Note: Using v21 to ensure it's higher than previous v20, as Dexie requires increasing version numbers
    this.version(21).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, client_id, syncedOffline, created_at, template_id, status, synced, is_offline_draft',
      assessmentGoals: '++id, client_id, assessment_id, status, priority, due_date, created_at, updated_at, synced'
    });

    // Version 22 - Add localStorage and drafts tables to replace browser localStorage
    this.version(22).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, client_id, syncedOffline, created_at, template_id, status, synced, is_offline_draft',
      assessmentGoals: '++id, client_id, assessment_id, status, priority, due_date, created_at, updated_at, synced',
      localStorage: 'key, updated_at',
      drafts: '++id, type, client_id, document_id, [type+client_id], [type+client_id+document_id], updated_at'
    });

    // Version 23 - Add encryption support with biometric credentials and lock settings
    this.version(23).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, client_id, syncedOffline, created_at, template_id, status, synced, is_offline_draft',
      assessmentGoals: '++id, client_id, assessment_id, status, priority, due_date, created_at, updated_at, synced',
      localStorage: 'key, updated_at',
      drafts: '++id, type, client_id, document_id, [type+client_id], [type+client_id+document_id], updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key'
    }).upgrade(async trans => {
      logger.info('Upgrading to v23 with encryption support');

      // Initialize lock settings with default 10-minute timeout
      await trans.table('lockSettings').add({
        key: 'timeout',
        timeoutMinutes: 10,
        updated_at: new Date().toISOString()
      });
    });

    // Version 24 - Removed TOTP support (WebAuthn has built-in PIN fallback)
    this.version(24).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, client_id, syncedOffline, created_at, template_id, status, synced, is_offline_draft',
      assessmentGoals: '++id, client_id, assessment_id, status, priority, due_date, created_at, updated_at, synced',
      localStorage: 'key, updated_at',
      drafts: '++id, type, client_id, document_id, [type+client_id], [type+client_id+document_id], updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      totpCredentials: null // Delete totpCredentials table
    }).upgrade(async _trans => {
      logger.info('Upgrading to v24: Removed TOTP (WebAuthn has PIN fallback)');
    });

    // Version 25 - Rename localStorage to applicationSession for clarity
    this.version(25).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, client_id, syncedOffline, created_at, template_id, status, synced, is_offline_draft',
      assessmentGoals: '++id, client_id, assessment_id, status, priority, due_date, created_at, updated_at, synced',
      applicationSession: 'key, updated_at',
      drafts: '++id, type, client_id, document_id, [type+client_id], [type+client_id+document_id], updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      localStorage: null // Rename localStorage to applicationSession
    }).upgrade(async trans => {
      logger.info('Upgrading to v25: Renaming localStorage to applicationSession');

      // Copy data from localStorage to applicationSession
      const oldData = await trans.table('localStorage').toArray();
      if (oldData.length > 0) {
        await trans.table('applicationSession').bulkAdd(oldData);
        logger.info(`Migrated ${oldData.length} items from localStorage to applicationSession`);
      }
    });

    // Version 26 - Add signatures table for offline signature collection
    this.version(26).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, client_id, syncedOffline, created_at, template_id, status, synced, is_offline_draft, locally_finalized',
      assessmentGoals: '++id, client_id, assessment_id, status, priority, due_date, created_at, updated_at, synced',
      applicationSession: 'key, updated_at',
      drafts: '++id, type, client_id, document_id, [type+client_id], [type+client_id+document_id], updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed'
    });

    // Version 27 - Add local_id to documents for draft identification
    this.version(27).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, syncedOffline, created_at, template_id, status, synced, is_offline_draft, locally_finalized',
      assessmentGoals: '++id, client_id, assessment_id, status, priority, due_date, created_at, updated_at, synced',
      applicationSession: 'key, updated_at',
      drafts: '++id, type, client_id, document_id, [type+client_id], [type+client_id+document_id], updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed'
    });

    // Version 28 - Remove redundant syncedOffline and is_offline_draft from documents
    // These are replaced by: local_id (identifies drafts) and synced (tracks sync state)
    this.version(28).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized',
      assessmentGoals: '++id, client_id, assessment_id, status, priority, due_date, created_at, updated_at, synced',
      applicationSession: 'key, updated_at',
      drafts: '++id, type, client_id, document_id, [type+client_id], [type+client_id+document_id], updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed'
    });

    // Version 29 - Remove assessmentGoals and drafts tables (Notepad and Goals features removed)
    this.version(29).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized',
      applicationSession: 'key, updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed',
      assessmentGoals: null, // Delete assessmentGoals table
      drafts: null // Delete drafts table
    });

    // Version 30 - Standardize document status to 'completed' (migrate 'complete' -> 'completed')
    this.version(30).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized',
      applicationSession: 'key, updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed'
    }).upgrade(async tx => {
      // Migrate all documents with status 'complete' to 'completed'
      const documents = await tx.table('documents').where('status').equals('complete').toArray();
      for (const doc of documents) {
        await tx.table('documents').update(doc.id, { status: 'completed' });
      }
    });

    // Version 31 - Add helpDocuments table for offline help documentation
    this.version(31).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized',
      applicationSession: 'key, updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed',
      helpDocuments: 'slug, id, updated_at'
    });

    // Version 32 - Add analyticsEvents table for analytics tracking
    this.version(32).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized',
      applicationSession: 'key, updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed',
      analyticsEvents: 'id, event_type, person_id, timestamp, created_at',
      helpDocuments: 'slug, id, updated_at'
    })

    // Version 33 - Add contacts table for client contacts
    this.version(33).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      contacts: 'id, customer_id, organization_id, created_at, updated_at, [customer_id+organization_id]',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized',
      applicationSession: 'key, updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed',
      analyticsEvents: 'id, event_type, person_id, timestamp, created_at',
      helpDocuments: 'slug, id, updated_at'
    })

    // Version 34 - Remove notes table
    this.version(34).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      contacts: 'id, customer_id, organization_id, created_at, updated_at, [customer_id+organization_id]',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized',
      notes: null, // Delete notes table
      applicationSession: 'key, updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      signatures: 'local_id, id, document_id, status, local_completed',
      analyticsEvents: 'id, event_type, person_id, timestamp, created_at',
      helpDocuments: 'slug, id, updated_at'
    });

    // Version 35 - Add CRDT metadata support for conflict-free document merging
    this.version(35).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      contacts: 'id, customer_id, organization_id, created_at, updated_at, [customer_id+organization_id]',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized, client_uuid',
      applicationSession: 'key, updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      fieldNotes: 'local_id, id, document_id, scope, synced, deleted',
      signatures: 'local_id, id, document_id, status, local_completed',
      analyticsEvents: 'id, event_type, person_id, timestamp, created_at',
      helpDocuments: 'slug, id, updated_at'
    }).upgrade(async tx => {
      logger.info('Upgrading to v35: Initializing CRDT fields for existing documents');
      const documents = await tx.table('documents').toArray();
      for (const doc of documents) {
        const key = doc.id ?? doc.local_id;
        if (key == null) continue; // Skip truly broken records
        await tx.table('documents').update(key, {
          super_form_meta: doc.super_form_meta || {},
          locally_finalized: doc.locally_finalized ?? false,
        });
      }
    });

    // Version 36 - Drop fieldNotes table (notes now stored as field_notes.* keys in super_form)
    this.version(36).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id',
      contacts: 'id, customer_id, organization_id, created_at, updated_at, [customer_id+organization_id]',
      documentTemplates: 'id, label, organization_id',
      documents: 'id, local_id, client_id, created_at, template_id, status, synced, locally_finalized, client_uuid',
      applicationSession: 'key, updated_at',
      biometricCredentials: 'userId',
      lockSettings: 'key',
      fieldNotes: null, // Delete fieldNotes table
      signatures: 'local_id, id, document_id, status, local_completed',
      analyticsEvents: 'id, event_type, person_id, timestamp, created_at',
      helpDocuments: 'slug, id, updated_at'
    });

    // Version 37 - Add programs and sites tables for name lookups
    this.version(37).stores({
      programs: 'id, organization_id',
      sites: 'id, organization_id'
    });

    // Version 38 - Add compound index [organization_id+updated_at] on clients
    // Enables efficient "top N clients by updated_at within an org" queries
    this.version(38).stores({
      clients: 'id, label, syncedOffline, updated_at, last_viewed_at, eTag, lastModified, organization_id, [organization_id+updated_at]',
    });

    // Version 39 - Add updated_at index on documents
    // Enables orderBy('updated_at').reverse() to find the latest synced document
    // without loading all matching documents into memory first
    this.version(39).stores({
      documents: 'id, local_id, client_id, created_at, updated_at, template_id, status, synced, locally_finalized, client_uuid',
    });

    // Add sync_error_type and sync_error_message to documents (non-indexed),
    // program_disabled to documentTemplates (non-indexed),
    // has_offline_access to person data (non-indexed).
    // No schema change needed — just a version bump for the migration.
    this.version(40).stores({});

    // Version 41 - Drop lockSettings table; auto-lock timeout is no longer user-configurable
    this.version(41).stores({
      lockSettings: null
    });

    // Version 42 - Scope signature local_ids by document ("<document_id>:<slot>").
    // local_id is this table's primary key and the bare slot ("title-3") repeats
    // across documents sharing a signature config, so unscoped rows silently
    // overwrote each other. Data rewrite only — the keyPath is unchanged.
    this.version(42).stores({}).upgrade(async (tx) => {
      const table = tx.table<Signature, string>('signatures');
      const rows = await table.toArray();
      const legacy = rows.filter(row => typeof row.local_id === 'string' && !row.local_id.includes(':') && row.document_id != null);
      if (legacy.length === 0) return;

      await table.bulkDelete(legacy.map(row => row.local_id));
      await table.bulkPut(legacy.map(row => ({
        ...row,
        local_id: scopedLocalId(row.document_id, row.local_id)
      })));
    });

    // Version 43 - Add sync_error_fingerprint, sync_error_at and
    // sync_error_stage to documents (all non-indexed): the
    // digest of the payload the server refused, so a deterministic rejection
    // stops being re-sent on every sweep. Also index sync_error_type, which
    // Settings > Sync queries on: it is null for a healthy document and
    // IndexedDB does not index null, so the index holds only the rows that
    // actually have an error instead of forcing a scan of every document.
    this.version(43).stores({
      documents: 'id, local_id, client_id, created_at, updated_at, template_id, status, synced, locally_finalized, client_uuid, sync_error_type',
    });

    // Version 44 - Conditional-GET state (ETags, Last-Modified, page manifests)
    // moves out of applicationSession into its own table, one row per resource,
    // scoped by organization and indexed by collection so a client's pages can
    // be forgotten together, and by updated_at so stale rows can be swept. Its
    // validators and manifests are encrypted (see the encryption config); the
    // old rows are dropped rather than migrated — a cold cache costs one full
    // fetch per page, once. (Dexie 4 adds an index declared at an unchanged
    // version to already-opened databases, so no bump is needed for one.)
    this.version(44).stores({
      httpCache: HTTP_CACHE_SCHEMA,
    }).upgrade(async (tx) => {
      const session = tx.table<{ key: string }, string>('applicationSession');
      const keys = await session.toCollection().primaryKeys();
      const stale = keys.filter(key => /_(etag|last_modified|manifest)$/.test(key));
      if (stale.length > 0) await session.bulkDelete(stale);
    });
  }
}

// Create and export the database instance
const db = new MonAmiDatabase();

// Track whether encryption is currently enabled
let encryptionEnabled = false;

/**
 * Another tab deleted the database (logout runs `db.delete()`).
 *
 * Dexie's default reaction is to close and then silently auto-reopen on the
 * next query, which recreates an empty database while this tab's encryption
 * middleware still holds the old key. Once the other tab registers a new
 * passkey (fresh salt, fresh key) and writes `current_person`, this tab's live
 * queries decrypt with the wrong key and throw inside render. The data is gone
 * either way, so close for good and reload: module state resets and the app
 * lands on the login screen like the tab that logged out. Upgrades
 * (`newVersion > 0`) are left to Dexie's default handling.
 */
export function handleDatabaseDeletedElsewhere(
  event: IDBVersionChangeEvent,
  reload: () => void = () => window.location.reload(),
): void {
  if (event.newVersion !== null) return;
  logger.warn('Database deleted by another tab — closing and reloading');
  db.close({ disableAutoOpen: true });
  encryptionEnabled = false;
  reload();
}
db.on('versionchange', handleDatabaseDeletedElsewhere);

// Track database opening promise to prevent race conditions
let openingPromise: Promise<void> | null = null;
// Boolean lock flag to prevent race condition between db.isOpen() check and setting openingPromise
let isOpening = false;

/**
 * Begin encrypted access to the database with the provided key
 * This should be called after successful biometric authentication
 *
 * Note: This doesn't "enable" encryption - data is always encrypted at rest.
 * This function provides the key to ACCESS the encrypted data.
 *
 * @param encryptionKey - Uint8Array of 32 bytes derived from biometric credential
 */
export async function beginEncryptedAccess(encryptionKey: Uint8Array): Promise<void> {
  if (encryptionEnabled) {
    logger.warn('Encryption is already enabled');
    return;
  }

  // Wait for any pending transactions to complete before closing.
  // This ensures credentials are fully written to IndexedDB. Skip when
  // the db is already closed (e.g. the passkey-migration re-key flow,
  // which calls endEncryptedAccess immediately before this) — there's
  // nothing to flush, and a transaction on a closed db would throw
  // DatabaseClosedError and leave the db permanently closed.
  if (db.isOpen()) {
    await db.transaction('r', db.biometricCredentials, async () => {
      await db.biometricCredentials.count();
    });
    db.close();
  }

  // Configure encryption for PHI/PII tables
  // IMPORTANT: All tables containing sensitive patient data MUST be encrypted
  // to ensure data is protected at rest when the screen is locked.
  await applyEncryptionMiddleware(
    db,
    encryptionKey,
    {
      // Encrypt client PHI fields
      clients: {
        type: 'ENCRYPT_LIST',
        fields: ['encrypted_label', 'person', 'address', 'external_id']
      },
      // Encrypt contact PHI fields
      contacts: {
        type: 'ENCRYPT_LIST',
        fields: ['person', 'address', 'note', 'relationship']
      },
      // Encrypt document content
      documents: {
        type: 'ENCRYPT_LIST',
        fields: ['name', 'data', 'super_form', 'pdfContent', 'warning_dismissals', 'finalize_warnings']
      },
      // Encrypt applicationSession values (contains current_person and other sensitive data)
      applicationSession: {
        type: 'ENCRYPT_LIST',
        fields: ['value']
      },
      // Encrypt signature data (contains names which is PHI)
      signatures: {
        type: 'ENCRYPT_LIST',
        fields: ['name_confirmation', 'signatory', 'procurator', 'title']
      },
      // Encrypt conditional-GET state: validators and the per-page manifests
      // (which record which document ids belong to a client, and which client
      // ids a person is assigned). key / organization_id / collection / name
      // stay plaintext: they are the primary key and the compound index, which
      // dexie-encrypted cannot encrypt without breaking forgetCollection's
      // lookup, and the ids they embed are already plaintext indexes elsewhere
      // (documents.client_id, clients.organization_id).
      httpCache: {
        type: 'ENCRYPT_LIST',
        fields: ['etag', 'last_modified', 'manifest']
      },
      // Tables NOT encrypted (no PHI/PII):
      // - documentTemplates: Assessment form templates (no patient data)
      // - biometricCredentials: WebAuthn credentials (authentication data only)
    },
    async () => {
      // Clear callback - called when encryption key is cleared
      logger.info('Encryption key cleared from database');
      encryptionEnabled = false;
    }
  );

  // Reopen the database
  await db.open();

  encryptionEnabled = true;
  logger.info('Encrypted access started');
}

/**
 * End encrypted access and clear the key from memory
 * This should be called when the screen is locked
 *
 * Note: This doesn't "disable" encryption - data remains encrypted at rest on disk.
 * This function removes the key from memory so encrypted data becomes inaccessible.
 *
 * THREAT MODEL DOCUMENTATION:
 * ===========================
 * This function closes the database and clears the encryption key from memory.
 * The encrypted data remains in IndexedDB on disk, but is inaccessible without the key.
 *
 * What this encryption PROTECTS against:
 * - Casual device access (unauthorized users cannot read encrypted data)
 * - Data extraction via browser DevTools (encrypted fields appear as gibberish)
 * - Accidental data exposure through screen sharing or screenshots
 * - Network interception (data is encrypted at rest and in transit)
 *
 * What this encryption DOES NOT protect against:
 * - Sophisticated forensic analysis with physical device access
 * - Cold boot attacks that extract memory contents
 * - OS-level compromise or malware with system-level access
 * - Brute force attacks on the WebAuthn credentials (mitigated by PIN rate limiting)
 *
 * Security considerations:
 * - Encryption uses AES-GCM (authenticated encryption)
 * - Keys derived via PBKDF2 with 100k iterations
 * - Domain separation prevents key reuse across contexts
 * - Session validity checked when online before unlock
 * - Offline access continues if session expired server-side (by design for offline-first)
 *
 * For compliance requirements (HIPAA, etc.), this provides appropriate protection
 * for PHI/PII stored on user devices as part of an offline-first PWA.
 */
export async function endEncryptedAccess(): Promise<void> {
  if (!encryptionEnabled) {
    return;
  }

  // Close the database to clear encryption keys from memory
  // Note: We don't use clearAllTables() as it may clear credential tables
  // The encryption key is stored in the middleware closure and will be
  // inaccessible once the database is closed
  db.close();

  encryptionEnabled = false;
  logger.info('Encrypted access ended');
}

/**
 * Check if encryption is currently enabled
 */
export function isEncryptionEnabled(): boolean {
  return encryptionEnabled;
}

/**
 * Can application data be read right now? An open database is not enough:
 * the lock screen opens it WITHOUT the key to read credentials, and every
 * encrypted table is ciphertext until beginEncryptedAccess has run. With
 * biometrics disabled (test and some dev builds) the tables are stored in the
 * clear and are readable whenever the database is open. Anything that reads
 * data on its own initiative (the reconnect refresh) gates on this.
 */
export function isDataReadable(): boolean {
  return db.isOpen() && (encryptionEnabled || BIOMETRICS_DISABLED);
}

/**
 * Ensure the database is open
 * Reopens the database if it's currently closed
 * Prevents race conditions by using an atomic boolean lock flag
 * This is needed when unlocking the screen to read credentials
 */
export async function ensureDatabaseOpen(): Promise<void> {
  // Already open - nothing to do
  if (db.isOpen()) {
    return;
  }

  // CRITICAL: Check and set the lock flag atomically
  // This prevents race condition between db.isOpen() check and setting openingPromise
  if (isOpening) {
    // Another call is opening - wait for the promise
    if (openingPromise) {
      await openingPromise;
    }
    return;
  }

  // Set lock flag IMMEDIATELY (before any async operations)
  isOpening = true;

  try {
    // Double-check db.isOpen() after acquiring lock
    if (db.isOpen()) {
      return;
    }

    // We need to open the database
    logger.debug('Reopening database...');
    openingPromise = db.open()
      .then(async () => {
        logger.debug('Database reopened successfully');

        // Debug: Print IndexedDB storage size
        if ('storage' in navigator && 'estimate' in navigator.storage) {
          try {
            const estimate = await navigator.storage.estimate();
            const usageMB = ((estimate.usage || 0) / (1024 * 1024)).toFixed(2);
            const quotaMB = ((estimate.quota || 0) / (1024 * 1024)).toFixed(2);
            const percentUsed = estimate.quota ? ((estimate.usage || 0) / estimate.quota * 100).toFixed(2) : 'N/A';
            logger.debug(`IndexedDB Storage: ${usageMB} MB used / ${quotaMB} MB quota (${percentUsed}% used)`);
          } catch (error) {
            logger.error('Failed to estimate storage:', error);
          }
        }
      });

    await openingPromise;
  } finally {
    // Always clear both flags
    isOpening = false;
    openingPromise = null;
  }
}

/**
 * Apply encryption to a specific database instance
 * Useful for testing with isolated database instances
 *
 * @param dbInstance - The database instance to apply encryption to
 * @param encryptionKey - The encryption key to use
 * @param clearCallback - Optional callback when encryption is cleared
 * @returns Promise that resolves when encryption is applied
 */
export async function applyEncryptionToInstance(
  dbInstance: MonAmiDatabase,
  encryptionKey: Uint8Array,
  clearCallback?: (db: MonAmiDatabase) => Promise<void>
): Promise<void> {
  // Close the database before applying encryption
  if (dbInstance.isOpen()) {
    dbInstance.close();
  }

  // Configure encryption for PHI/PII tables
  await applyEncryptionMiddleware(
    dbInstance,
    encryptionKey,
    {
      // Encrypt client PHI fields
      clients: {
        type: 'ENCRYPT_LIST',
        fields: ['encrypted_label', 'person', 'address', 'external_id']
      },
      // Encrypt contact PHI fields
      contacts: {
        type: 'ENCRYPT_LIST',
        fields: ['person', 'address', 'note', 'relationship']
      },
      // Encrypt document content
      documents: {
        type: 'ENCRYPT_LIST',
        fields: ['name', 'data', 'super_form', 'pdfContent', 'warning_dismissals', 'finalize_warnings']
      },
      // Encrypt applicationSession values
      applicationSession: {
        type: 'ENCRYPT_LIST',
        fields: ['value']
      },
      // Encrypt signature data
      signatures: {
        type: 'ENCRYPT_LIST',
        fields: ['name_confirmation', 'signatory', 'procurator', 'title']
      },
      // Encrypt conditional-GET state (see the main config above)
      httpCache: {
        type: 'ENCRYPT_LIST',
        fields: ['etag', 'last_modified', 'manifest']
      },
      // Tables NOT encrypted (no PHI/PII): documentTemplates, biometricCredentials
    },
    clearCallback || (async () => {
      logger.info('Encryption key cleared from test database instance');
    })
  );

  // Reopen the database
  await dbInstance.open();
}

// Export the class for testing (allows creating isolated instances)
export { MonAmiDatabase };

export default db;
